import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Pagination,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  PersonOff as DeactivateIcon,
  PersonAdd as ReactivateIcon,
  VpnKey as ResetPasswordIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import proprietaireService, { ProprietaireResponse } from '../../services/proprietaireService';
import ProprietaireDetails from './ProprietaireDetails';

interface ProprietaireFormData {
  prenom: string;
  nom: string;
  email: string;
  telephone: string;
  adresse: string;
  code_postal: string;
  ville: string;
  pays: string;
}

const GestionProprietaires: React.FC = () => {
  const [proprietaires, setProprietaires] = useState<ProprietaireResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // États pour les dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [selectedProprietaire, setSelectedProprietaire] = useState<ProprietaireResponse | null>(null);
  const [newPassword, setNewPassword] = useState<string>('');

  // Détails propriétaire + animaux
  const [showEnhancedDetailDialog, setShowEnhancedDetailDialog] = useState(false);
  const [selectedProprietaireId, setSelectedProprietaireId] = useState<number | null>(null);


  // État du formulaire d'ajout
  const [formData, setFormData] = useState<ProprietaireFormData>({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    adresse: '',
    code_postal: '',
    ville: '',
    pays: 'France'
  });

  // Charger les propriétaires
  const loadProprietaires = async (currentPage: number = 1, search: string = '') => {
    try {
      setLoading(true);
      const response = await proprietaireService.getProprietaires(currentPage, search);
      setProprietaires(response.results);
      setTotalCount(response.count);
      setTotalPages(Math.ceil(response.count / 20)); // 20 par page
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des propriétaires');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProprietaires(page, searchTerm);
  }, [page, searchTerm]);

  // Recherche avec délai
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== undefined) {
        setPage(1);
        loadProprietaires(1, searchTerm);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Gérer le formulaire d'ajout
  const handleFormChange = (field: keyof ProprietaireFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Ajouter un propriétaire
  const handleAddProprietaire = async () => {
    try {
      await proprietaireService.createProprietaire(formData);
      setShowAddDialog(false);
      setFormData({
        prenom: '',
        nom: '',
        email: '',
        telephone: '',
        adresse: '',
        code_postal: '',
        ville: '',
        pays: 'France'
      });
      loadProprietaires(page, searchTerm);
    } catch (err) {
      setError('Erreur lors de la création du propriétaire');
      console.error('Erreur:', err);
    }
  };

  // Désactiver/Réactiver un propriétaire
  const handleToggleActive = async (proprietaire: ProprietaireResponse) => {
    try {
      if (proprietaire.user.is_active) {
        await proprietaireService.deactivateProprietaire(proprietaire.id);
      } else {
        await proprietaireService.reactivateProprietaire(proprietaire.id);
      }
      loadProprietaires(page, searchTerm);
    } catch (err) {
      setError('Erreur lors de la modification du statut');
      console.error('Erreur:', err);
    }
  };

  // Réinitialiser le mot de passe
  const handleResetPassword = async (proprietaire: ProprietaireResponse) => {
    try {
      const response = await proprietaireService.resetPassword(proprietaire.id);
      setNewPassword(response.new_password);
      setSelectedProprietaire(proprietaire);
      setShowPasswordReset(true);
      loadProprietaires(page, searchTerm);
    } catch (err) {
      setError('Erreur lors de la réinitialisation du mot de passe');
      console.error('Erreur:', err);
    }
  };


  const handleViewEnhancedDetails = (proprietaire: ProprietaireResponse) => {
    console.log('handleViewEnhancedDetails appelé pour:', proprietaire);
    alert(`Ouverture des détails pour ${proprietaire.prenom} ${proprietaire.nom}`);
    setSelectedProprietaireId(proprietaire.id);
    setShowEnhancedDetailDialog(true);
  };

  const handleProprietaireUpdate = (updatedProprietaire: ProprietaireResponse) => {
    // Mettre à jour la liste des propriétaires
    setProprietaires(prev => 
      prev.map(p => p.id === updatedProprietaire.id ? updatedProprietaire : p)
    );
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  if (loading && proprietaires.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" component="h1">
              👥 Gestion des Propriétaires
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddDialog(true)}
            >
              Ajouter un propriétaire
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Barre de recherche */}
          <Box mb={3}>
            <TextField
              fullWidth
              placeholder="Rechercher par nom, prénom, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Statistiques */}
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary">
              {totalCount} propriétaire(s) au total
            </Typography>
          </Box>

          {/* Tableau des propriétaires */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nom</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Téléphone</TableCell>
                  <TableCell>Ville</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Vétérinaire</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Inscription</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {proprietaires.map((proprietaire) => (
                  <TableRow key={proprietaire.id}>
                    <TableCell>
                      <Button
                        variant="text"
                        onClick={() => handleViewEnhancedDetails(proprietaire)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 'bold',
                          color: 'primary.main',
                          textAlign: 'left',
                          justifyContent: 'flex-start',
                          p: 0,
                          minWidth: 'auto',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline',
                          }
                        }}
                      >
                        {proprietaire.prenom} {proprietaire.nom}
                      </Button>
                    </TableCell>
                    <TableCell>{proprietaire.email}</TableCell>
                    <TableCell>{proprietaire.telephone}</TableCell>
                    <TableCell>{proprietaire.ville || '-'}</TableCell>
                    <TableCell>
                      <code>{proprietaire.user.username}</code>
                    </TableCell>
                    <TableCell>
                      {proprietaire.user.veterinaire_selectionne_nom || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={proprietaire.user.is_active ? 'Actif' : 'Inactif'}
                        color={proprietaire.user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(proprietaire.user.date_joined)}</TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={1} justifyContent="center">
                        <Tooltip title="Voir les détails complets">
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => handleViewEnhancedDetails(proprietaire)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Modifier">
                          <IconButton size="small" color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Réinitialiser le mot de passe">
                          <IconButton 
                            size="small" 
                            color="warning"
                            onClick={() => handleResetPassword(proprietaire)}
                          >
                            <ResetPasswordIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title={proprietaire.user.is_active ? 'Désactiver' : 'Réactiver'}>
                          <IconButton
                            size="small"
                            color={proprietaire.user.is_active ? 'error' : 'success'}
                            onClick={() => handleToggleActive(proprietaire)}
                          >
                            {proprietaire.user.is_active ? <DeactivateIcon /> : <ReactivateIcon />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>


      {/* Dialog d'ajout de propriétaire */}
      <Dialog 
        open={showAddDialog} 
        onClose={() => setShowAddDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>➕ Ajouter un nouveau propriétaire</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Box display="flex" gap={2}>
              <TextField
                label="Prénom"
                value={formData.prenom}
                onChange={(e) => handleFormChange('prenom', e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Nom"
                value={formData.nom}
                onChange={(e) => handleFormChange('nom', e.target.value)}
                required
                fullWidth
              />
            </Box>
            
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
              required
              fullWidth
            />
            
            <TextField
              label="Téléphone"
              value={formData.telephone}
              onChange={(e) => handleFormChange('telephone', e.target.value)}
              required
              fullWidth
            />
            
            <TextField
              label="Adresse"
              value={formData.adresse}
              onChange={(e) => handleFormChange('adresse', e.target.value)}
              fullWidth
            />
            
            <Box display="flex" gap={2}>
              <TextField
                label="Code postal"
                value={formData.code_postal}
                onChange={(e) => handleFormChange('code_postal', e.target.value)}
                fullWidth
              />
              <TextField
                label="Ville"
                value={formData.ville}
                onChange={(e) => handleFormChange('ville', e.target.value)}
                fullWidth
              />
            </Box>
            
            <TextField
              label="Pays"
              value={formData.pays}
              onChange={(e) => handleFormChange('pays', e.target.value)}
              fullWidth
            />

              <Alert severity="info">
              <strong>Informations de connexion :</strong><br />
              • Username : sera généré automatiquement (prenom.nom)<br />
              • Mot de passe : <code>temp123</code> (à changer lors de la première connexion)<br />
              • <strong>Le propriétaire sera automatiquement associé à votre compte vétérinaire</strong>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleAddProprietaire}
            variant="contained"
            disabled={!formData.prenom || !formData.nom || !formData.email || !formData.telephone}
          >
            Créer le propriétaire
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de réinitialisation de mot de passe */}
      <Dialog 
        open={showPasswordReset} 
        onClose={() => setShowPasswordReset(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>🔑 Nouveau mot de passe généré</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            Le mot de passe a été réinitialisé avec succès !
          </Alert>
          
          {selectedProprietaire && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Propriétaire :</strong> {selectedProprietaire.prenom} {selectedProprietaire.nom}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Username :</strong> <code>{selectedProprietaire.user.username}</code>
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Nouveau mot de passe :</strong> 
                <Box 
                  component="span" 
                  sx={{ 
                    backgroundColor: '#f5f5f5', 
                    padding: '4px 8px', 
                    borderRadius: 1, 
                    fontFamily: 'monospace',
                    fontSize: '1.1em',
                    ml: 1
                  }}
                >
                  {newPassword}
                </Box>
              </Typography>
              
              <Alert severity="warning" sx={{ mt: 2 }}>
                Communiquez ce mot de passe au propriétaire de manière sécurisée. 
                Il devra le changer lors de sa prochaine connexion.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPasswordReset(false)} variant="contained">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog vue détaillée améliorée */}
      <Dialog
        open={showEnhancedDetailDialog}
        onClose={() => setShowEnhancedDetailDialog(false)}
        maxWidth="xl"
        fullWidth
        fullScreen
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedProprietaireId && (
            <ProprietaireDetails
              proprietaireId={selectedProprietaireId}
              onClose={() => setShowEnhancedDetailDialog(false)}
              onUpdate={handleProprietaireUpdate}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GestionProprietaires;
